import React, { useState } from 'react';
import { X, Save, RefreshCw, CalendarDays } from 'lucide-react';
import api from '../../../api/axios';
import { toast } from 'react-hot-toast';
import { useLoading } from '../../../context/LoadingContext';
import styles from '../EmployeeDrawer.module.css';
import CustomSelect from '../../../components/Common/Select/CustomSelect';

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 8 }, (_, i) => {
  const y = currentYear + 1 - i;
  return { value: y, label: String(y) };
});

const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const BulkGeneratePayrollDrawer = ({ isOpen, onClose, onSuccess }) => {
  const { showLoader, hideLoader } = useLoading();
  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: currentYear,
  });
  const [isGenerating, setIsGenerating] = useState(false);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    if (!formData.month || !formData.year) {
      toast.error('Please select both Month and Year');
      return;
    }

    setIsGenerating(true);
    showLoader();
    try {
      const response = await api.post('/payroll/bulk-generate', formData);
      if (response.data.success) {
        toast.success(response.data.message || 'Bulk payroll generation started successfully');
        if (onSuccess) onSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start bulk generation');
    } finally {
      setIsGenerating(false);
      hideLoader();
    }
  };

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>Bulk Generate Payroll</h2>
            <p>Schedule payroll calculation for all active employees</p>
          </div>
          <button type="button" className={styles['close-btn']} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content Body */}
        <div className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            
            <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px', display: 'flex', gap: '12px' }}>
              <CalendarDays style={{ color: '#2563eb', flexShrink: 0 }} size={22} />
              <div>
                <h4 style={{ margin: '0 0 4px 0', color: '#1e3a8a', fontSize: '14px', fontWeight: '700' }}>Background Processing</h4>
                <p style={{ margin: 0, color: '#1e40af', fontSize: '13px', lineHeight: '1.4' }}>
                  Selecting a month and year will queue a background task. The system will process each active employee individually, factoring in attendance, loans, advances, and paid holidays.
                </p>
              </div>
            </div>

            <div className={styles['form-group']}>
              <label>Select Month <span className={styles['required-star']}>*</span></label>
              <CustomSelect
                value={months.find(m => m.value === Number(formData.month)) || null}
                onChange={(opt) => setFormData(prev => ({ ...prev, month: opt ? opt.value : '' }))}
                options={months}
                placeholder="Select Month"
                isSearchable={false}
              />
            </div>

            <div className={styles['form-group']}>
              <label>Select Year <span className={styles['required-star']}>*</span></label>
              <CustomSelect
                value={yearOptions.find(y => y.value === Number(formData.year)) || null}
                onChange={(opt) => setFormData(prev => ({ ...prev, year: opt ? opt.value : '' }))}
                options={yearOptions}
                placeholder="Select Year"
                isSearchable={false}
              />
            </div>

          </div>

          {/* Footer Actions */}
          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose} disabled={isGenerating}>
              Cancel
            </button>
            <button type="button" className={styles['save-btn']} onClick={handleGenerate} disabled={isGenerating}>
              {isGenerating ? <RefreshCw size={18} className="spin" /> : <Save size={18} />}
              <span>Start Generation</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BulkGeneratePayrollDrawer;
